import React, { useState, useMemo, useEffect, useRef } from 'react';
import './GameBoard.css';
import { getAIPlayerById } from '../config/ai-players-config';
import { classifyPolyomino, checkWin } from '../config/patterns.js';
import { calculatePatternHeatmap } from '../config/patterns';
import { getAnalysis, getHeatmap } from '../config/oracle-ai';

// Function to create high-quality scaled image using canvas
const createScaledImage = (src, targetSize) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      canvas.width = targetSize;
      canvas.height = targetSize;
      
      // Enable high-quality scaling
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      // Draw the scaled image
      ctx.drawImage(img, 0, 0, targetSize, targetSize);
      
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = src;
  });
};

// Import the domino tiling generation function
const generateDominoTilings = (boardSize) => {
  // Pattern 2: Horizontal domino tiling (actual dominos, twice as wide as high)
  const horizontalDominoTiling = [];
  
  // Create a virtual board that extends beyond the actual board to handle edge clipping
  const virtualBoardSize = boardSize + 2; // Add 1 column on each side
  const virtualTiling = [];
  let dominoCounter = 1;
  
  for (let row = 0; row < boardSize; row++) {
    const virtualRow = [];
    const offset = row % 2; // Offset every other row
    
    for (let col = -1; col <= boardSize; col++) { // Include virtual columns
      const adjustedCol = col + offset;
      const isStartOfDomino = adjustedCol % 2 === 0;
      
      if (isStartOfDomino) {
        dominoCounter++;
      }
      
      virtualRow.push(dominoCounter);
    }
    virtualTiling.push(virtualRow);
  }
  
  // Extract the actual board portion (columns 0 to boardSize-1)
  for (let row = 0; row < boardSize; row++) {
    const tilingRow = [];
    for (let col = 0; col < boardSize; col++) {
      tilingRow.push(virtualTiling[row][col + 1]); // +1 because we added a virtual column on the left
    }
    horizontalDominoTiling.push(tilingRow);
  }

  // Pattern 5: Aligned horizontal domino tiling
  const alignedDominoTiling = [];
  let dominoCounter5 = 0;
  for (let row = 0; row < boardSize; row++) {
    const tilingRow = [];
    for (let col = 0; col < boardSize; col++) {
      // Increment counter only when we start a new domino
      if (col % 2 === 0) {
        dominoCounter5++;
      }
      
      tilingRow.push(dominoCounter5);
    }
    alignedDominoTiling.push(tilingRow);
  }

  // Pattern 6: Offset middle domino tiling
  const offsetMiddleDominoTiling = [];
  
  // Define the exact numbers from the user's matrix
  const patternNumbers = [
    [1, 1, 2, 2, 3, 3, 4, 4, 5, 5, 6, 6, 7, 7, 8, 8],
    [9, 9, 10, 10, 11, 11, 12, 12, 13, 13, 14, 14, 15, 15, 16, 16],
    [17, 18, 18, 19, 19, 20, 20, 21, 21, 22, 22, 23, 23, 24, 24],
    [25, 26, 26, 27, 27, 28, 28, 29, 29, 30, 30, 31, 31, 32, 32],
    [33, 33, 34, 34, 35, 35, 36, 36, 37, 37, 38, 38, 39, 39, 40, 40],
    [41, 41, 42, 42, 43, 43, 44, 44, 45, 45, 46, 46, 47, 47, 48, 48],
    [49, 50, 50, 51, 51, 52, 52, 53, 53, 54, 54, 55, 55, 56, 56],
    [57, 58, 58, 59, 59, 60, 60, 61, 61, 62, 62, 63, 63, 64, 64],
    [65, 65, 66, 66, 67, 67, 68, 68, 69, 69, 70, 70, 71, 71, 72, 72],
    [73, 73, 74, 74, 75, 75, 76, 76, 77, 77, 78, 78, 79, 79, 80, 80],
    [81, 82, 82, 83, 83, 84, 84, 85, 85, 86, 86, 87, 87, 88, 88],
    [89, 90, 90, 91, 91, 92, 92, 93, 93, 94, 94, 95, 95, 96, 96],
    [97, 97, 98, 98, 99, 99, 100, 100, 101, 101, 102, 102, 103, 103, 104, 104],
    [105, 105, 106, 106, 107, 107, 108, 108, 109, 109, 110, 110, 111, 111, 112, 112],
    [113, 114, 114, 115, 115, 116, 116, 117, 117, 118, 118, 119, 119, 120, 120]
  ];
  
  for (let row = 0; row < boardSize; row++) {
    const tilingRow = [];
    for (let col = 0; col < boardSize; col++) {
      // Map directly from the pattern numbers matrix
      const patternRow = row % 15; // 15 rows in the pattern
      const patternCol = col % 16; // 16 columns in the pattern
      
      // Get the number from the pattern, or use a fallback if out of bounds
      const dominoNumber = patternNumbers[patternRow] && patternNumbers[patternRow][patternCol] !== undefined 
        ? patternNumbers[patternRow][patternCol] 
        : 1; // fallback
      
      tilingRow.push(dominoNumber);
    }
    offsetMiddleDominoTiling.push(tilingRow);
  }

  // Pattern 4: Mixed orientation domino tiling
  const mixedOrientationTiling = [];
  for (let row = 0; row < boardSize; row++) {
    const tilingRow = [];
    for (let col = 0; col < boardSize; col++) {
      // Pattern 4: Mixed Orientation Domino Tiling (6x6 repeating pattern)
      // Simple alternating pattern: 2V, 2H, 2V, 2H, 2V, 2H across each row
      const patternRow = row % 6;
      const patternCol = col % 6;
      const patternBlockRow = Math.floor(row / 6);
      const patternBlockCol = Math.floor(col / 6);
      
      let dominoValue = 0;
      
      // Calculate base value for this 6x6 block
      const baseValue = (patternBlockRow * 3 + patternBlockCol) * 18;
      
      // Determine which group of 2 columns we're in (0, 1, 2, or 3 for 8x8 boards)
      const groupIndex = Math.floor(col / 2);
      
      if (patternRow < 2) { // Rows 0-1: Start with 2V, then 2H, then 2V, then 2H
        if (groupIndex === 0) { // Columns 0-1: 2 vertical dominos side-by-side
          dominoValue = baseValue + patternCol;
        } else if (groupIndex === 1) { // Columns 2-3: 2 horizontal dominos stacked
          dominoValue = baseValue + 2 + (patternRow % 2);
        } else if (groupIndex === 2) { // Columns 4-5: 2 vertical dominos side-by-side
          dominoValue = baseValue + 4 + (patternCol - 4);
        } else { // Columns 6-7: 2 horizontal dominos stacked (continuing pattern)
          dominoValue = baseValue + 6 + (patternRow % 2);
        }
      } else if (patternRow < 4) { // Rows 2-3: Start with 2H, then 2V, then 2H, then 2V
        if (groupIndex === 0) { // Columns 0-1: 2 horizontal dominos stacked
          dominoValue = baseValue + 8 + (patternRow % 2);
        } else if (groupIndex === 1) { // Columns 2-3: 2 vertical dominos side-by-side
          dominoValue = baseValue + 10 + (patternCol - 2);
        } else if (groupIndex === 2) { // Columns 4-5: 2 horizontal dominos stacked
          dominoValue = baseValue + 12 + (patternRow % 2);
        } else { // Columns 6-7: 2 vertical dominos side-by-side (continuing pattern)
          dominoValue = baseValue + 14 + (patternCol - 6);
        }
      } else { // Rows 4-5: Start with 2V, then 2H, then 2V, then 2H
        if (groupIndex === 0) { // Columns 0-1: 2 vertical dominos side-by-side
          dominoValue = baseValue + 16 + patternCol;
        } else if (groupIndex === 1) { // Columns 2-3: 2 horizontal dominos stacked
          dominoValue = baseValue + 18 + (patternRow % 2);
        } else if (groupIndex === 2) { // Columns 4-5: 2 vertical dominos side-by-side
          dominoValue = baseValue + 20 + (patternCol - 4);
        } else { // Columns 6-7: 2 horizontal dominos stacked (continuing pattern)
          dominoValue = baseValue + 22 + (patternRow % 2);
        }
      }
      
      tilingRow.push(dominoValue);
    }
    mixedOrientationTiling.push(tilingRow);
  }

  // Pattern 5: 2x3 block with vertical domino + two horizontal dominos stacked
  const verticalHorizontalTiling = [];
  for (let row = 0; row < boardSize; row++) {
    const tilingRow = [];
    for (let col = 0; col < boardSize; col++) {
      // Pattern repeats every 2 rows and every 3 columns
      const patternRow = row % 2;
      const patternCol = col % 3;
      const blockRow = Math.floor(row / 2);
      const blockCol = Math.floor(col / 3);
      
      // Calculate base value for this 2x3 block (3 dominos total)
      const baseValue = (blockRow * Math.ceil(boardSize / 3) + blockCol) * 3;
      
      let dominoValue = 0;
      
      if (patternCol === 0) { // First column: vertical domino
        // Vertical domino: same number for both rows in the column
        dominoValue = baseValue;
      } else if (patternCol === 1) { // Second column: first horizontal domino
        // First horizontal domino: same number for both rows
        dominoValue = baseValue + 1;
      } else { // Third column: second horizontal domino
        // Second horizontal domino: same number for both rows
        dominoValue = baseValue + 2;
      }
      
      tilingRow.push(dominoValue);
    }
    verticalHorizontalTiling.push(tilingRow);
  }

  return {
    pattern1: { pattern: horizontalDominoTiling },
    pattern2: { pattern: alignedDominoTiling },
    pattern3: { pattern: offsetMiddleDominoTiling },
    pattern4: { pattern: mixedOrientationTiling },
    pattern5: { pattern: verticalHorizontalTiling }
  };
};

const GameBoard = ({ 
  board, 
  onCellClick, 
  boardSize, 
  aiThinking, 
  winningPositions, 
  selectedAIPlayer1, 
  selectedAIPlayer2, 
  gameMode, 
  winningPattern, 
  showDominoHighlighting, 
  selectedAvatar, 
  selectedPattern,
  showHeatmap = false,
  showMoveEvaluation = false,
  useNashAnalysis = false
}) => {
  // Fixed board size - the board will always be 600x600px
  const fixedBoardSize = 400;
  const cellSize = fixedBoardSize / boardSize;

  // Generate domino tiling for debugging visualization (disable on 8x8 boards to prevent flashing)
  const getDominoTiling = () => {
    if (!winningPattern || boardSize === 8) return null; // Disable on 8x8 boards
    
    // Only show domino tiling for basic-loser patterns
    const patternClassification = classifyPolyomino(winningPattern);
    if (patternClassification !== 'basic-loser') {
      return null;
    }
    
    console.log(`🎯 Showing domino tiling for ${winningPattern} pattern (${patternClassification})`);
    const dominoTilings = generateDominoTilings(boardSize);
    
    // Use different tiling patterns based on the target pattern
    if (winningPattern === 'cross' || winningPattern === 'tee' || winningPattern === 'stair' || winningPattern === 'p' || winningPattern === 'l' || winningPattern === 'y') {
      return dominoTilings.pattern2.pattern;
    } else if (winningPattern === 'u' || winningPattern === 'c') {
      return dominoTilings.pattern3.pattern;
    } else if (winningPattern === 'i') {
      console.log('🔍 Using pattern4 for I pattern');
      console.log('🔍 Pattern4 tiling:', dominoTilings.pattern4.pattern);
      
      // Debug: Show the actual values for rows 6-7
      const tiling = dominoTilings.pattern4.pattern;
      console.log('🔍 Row 6 values:', tiling[6]);
      console.log('🔍 Row 7 values:', tiling[7]);
      
      return dominoTilings.pattern4.pattern;
    } else if (winningPattern === 's') {
      return dominoTilings.pattern5.pattern;
    } else {
      return dominoTilings.pattern1.pattern;
    }
  };

  const dominoTiling = getDominoTiling();
  
  // Move evaluation tooltip state
  const [tooltipData, setTooltipData] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipLoading, setTooltipLoading] = useState(false);
  
  // Cache for matrices (keyed by board state)
  const [matrixCache, setMatrixCache] = useState(null);
  const [cacheKey, setCacheKey] = useState('');
  const boardRef = useRef(null);
  
  // Generate cache key from board state
  const generateCacheKey = useMemo(() => {
    if (!board || !selectedPattern) return '';
    const key = `${selectedPattern}_${boardSize}_${JSON.stringify(board)}`;
    console.log('🔑 Generated cache key:', key.substring(0, 50) + '...', 'Pattern:', selectedPattern);
    return key;
  }, [board, selectedPattern, boardSize]);
  
  // Get complete score matrix when move evaluation is enabled
  const scoreMatrix = useMemo(() => {
    if (!showMoveEvaluation || !selectedPattern || !board || board.length === 0) {
      return null;
    }
    
    // This will be populated when we fetch the score matrix
    return null;
  }, [showMoveEvaluation, selectedPattern, board, boardSize]);
  
  // Get Nash analysis when enabled
  const nashAnalysis = useMemo(() => {
    if (!useNashAnalysis || !selectedPattern || !board || board.length === 0) {
      return null;
    }
    
    // This will be populated when we fetch the Nash analysis
    return null;
  }, [useNashAnalysis, selectedPattern, board, boardSize]);
  
  // Fetch complete score matrix for tooltips
  const fetchScoreMatrix = async () => {
    if (!showMoveEvaluation || !selectedPattern || !board || board.length === 0) {
      return null;
    }
    
    const currentCacheKey = generateCacheKey;
    if (matrixCache && cacheKey === currentCacheKey && matrixCache.scoreMatrix) {
      return matrixCache.scoreMatrix;
    }
    
    setTooltipLoading(true);
    try {
      // Determine backend base: dev (port 3000) -> PHP on 8000; prod -> relative
      const base = (typeof window !== 'undefined' && window.location && window.location.port === '3000')
        ? 'http://localhost:8000/'
        : '/';

      const response = await fetch(base + 'oracle.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          opcode: 'scorematrix',
          board: board,
          targetPattern: selectedPattern,
          boardSize: boardSize,
          aiMethod: 'nash',
          isFirstPlayer: true,
          randomnessLevel: 0.0
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.success && data.scoreMatrix) {
        // Cache the score matrix
        setMatrixCache(prev => ({
          ...prev,
          scoreMatrix: data.scoreMatrix,
          cacheKey: currentCacheKey
        }));
        return data.scoreMatrix;
      }
    } catch (error) {
      console.error('Error fetching score matrix:', error);
    } finally {
      setTooltipLoading(false);
    }
    
    return null;
  };
  
  // Fetch Nash equilibrium analysis
  const fetchNashAnalysis = async () => {
    if (!useNashAnalysis || !selectedPattern || !board || board.length === 0) {
      return null;
    }
    
    const currentCacheKey = generateCacheKey;
    if (matrixCache && cacheKey === currentCacheKey && matrixCache.nashAnalysis) {
      return matrixCache.nashAnalysis;
    }
    
    setTooltipLoading(true);
    try {
      // Determine backend base: dev (port 3000) -> PHP on 8000; prod -> relative
      const base = (typeof window !== 'undefined' && window.location && window.location.port === '3000')
        ? 'http://localhost:8000/'
        : '/';

      const response = await fetch(base + 'oracle.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          opcode: 'nashanalysis',
          board: board,
          targetPattern: selectedPattern,
          boardSize: boardSize,
          aiMethod: 'nash',
          isFirstPlayer: true,
          randomnessLevel: 0.0
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.success && data.nashAnalysis) {
        // Cache the Nash analysis
        setMatrixCache(prev => ({
          ...prev,
          nashAnalysis: data.nashAnalysis,
          nashMove: data.nashMove,
          cacheKey: currentCacheKey
        }));
        return data.nashAnalysis;
      }
    } catch (error) {
      console.error('Error fetching Nash analysis:', error);
    } finally {
      setTooltipLoading(false);
    }
    
    return null;
  };

  // Handle cell hover for move evaluation
  const handleCellMouseEnter = async (row, col, event) => {
    if ((!showMoveEvaluation && !useNashAnalysis) || board[row][col] !== null) {
      return;
    }
    
    // Validate row and col are within bounds
    if (row < 0 || row >= boardSize || col < 0 || col >= boardSize) {
      return;
    }
    
    // Set position immediately
    setTooltipPosition({
      x: event.clientX,
      y: event.clientY
    });
    
    let tooltipDataToSet = {};
    
    // Get score matrix data if enabled
    if (showMoveEvaluation) {
      let scoreData = matrixCache?.scoreMatrix;
      if (!scoreData) {
        scoreData = await fetchScoreMatrix();
      }
      
      if (scoreData) {
        const cellScore = scoreData.find(score => score.row === row && score.col === col);
        if (cellScore) {
          tooltipDataToSet = {
            ...tooltipDataToSet,
            aiThreat: cellScore.aiThreat || 0,
            opponentThreat: cellScore.opponentThreat || 0,
            opponentThreatAfterMove: cellScore.opponentThreatAfterMove || 0,
            targetMultiplicity: cellScore.targetMultiplicity || 0,
            weightedScore: cellScore.weightedScore || 0
          };
        }
      }
    }
    
    // Get Nash analysis data if enabled
    if (useNashAnalysis) {
      let nashData = matrixCache?.nashAnalysis;
      if (!nashData) {
        nashData = await fetchNashAnalysis();
      }
      
      if (nashData) {
        const cellNash = nashData.find(nash => nash.row === row && nash.col === col);
        if (cellNash) {
          tooltipDataToSet = {
            ...tooltipDataToSet,
            isNashMove: cellNash.isNashMove || false,
            nashStabilityScore: cellNash.nashStabilityScore || 0,
            bestResponseQuality: cellNash.bestResponseQuality || 0,
            equilibriumDeviation: cellNash.equilibriumDeviation || 0,
            opponentBestMove: cellNash.opponentBestMove || null
          };
        }
      }
    }
    
    if (Object.keys(tooltipDataToSet).length > 0) {
      setTooltipData({
        ...tooltipDataToSet,
        row: row,
        col: col
      });
    }
  };
  
  const handleCellMouseLeave = () => {
    setTooltipData(null);
    setTooltipLoading(false);
  };
  
  const handleBoardMouseLeave = () => {
    setTooltipData(null);
    setTooltipLoading(false);
  };
  
  // Clear cache when board changes
  useEffect(() => {
    if (cacheKey !== generateCacheKey) {
      console.log('🔄 Clearing cache - board changed:', { oldKey: cacheKey, newKey: generateCacheKey });
      setMatrixCache(null);
      setCacheKey('');
      setTooltipData(null);
      setTooltipLoading(false);
    }
  }, [generateCacheKey, cacheKey]);

  // Clear tooltip when pattern changes (additional safety)
  useEffect(() => {
    console.log('🎯 Pattern changed to:', selectedPattern);
    setTooltipData(null);
    setTooltipLoading(false);
  }, [selectedPattern]);
  
  // Global mouse move handler to hide tooltip when mouse leaves board area
  useEffect(() => {
    const handleGlobalMouseMove = (event) => {
      if (!showMoveEvaluation || !boardRef.current) return;
      
      const boardRect = boardRef.current.getBoundingClientRect();
      const mouseX = event.clientX;
      const mouseY = event.clientY;
      
      // Check if mouse is outside the board area
      if (mouseX < boardRect.left || mouseX > boardRect.right || 
          mouseY < boardRect.top || mouseY > boardRect.bottom) {
        setTooltipData(null);
        setTooltipLoading(false);
      }
    };
    
    if (showMoveEvaluation) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
    };
  }, [showMoveEvaluation]);
  
  // Generate consistent random colors for each domino value
  const getDominoColor = (dominoValue) => {
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
      '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
      '#F8C471', '#82E0AA', '#F1948A', '#85C1E9', '#D7BDE2',
      '#FAD7A0', '#A9DFBF', '#F9E79F'
    ];
    return colors[dominoValue % colors.length];
  };

  // Calculate heatmap if enabled (disable on 8x8 boards to prevent flashing)
  const heatmapData = showHeatmap && selectedPattern && board && board.length > 0 && boardSize !== 8 ? 
    calculatePatternHeatmap(board, selectedPattern, boardSize, 'X') : null;

  // Get heatmap color based on value
  const getHeatmapColor = (value) => {
    if (!value || value === 0 || !heatmapData) return 'transparent';
    
    // Color scale: green (low) -> yellow -> red (high)
    const maxValue = Math.max(...heatmapData.flat());
    if (maxValue === 0) return 'transparent';
    
    const normalizedValue = value / maxValue;
    
    if (normalizedValue < 0.3) {
      return `rgba(0, 255, 0, ${0.3 + normalizedValue * 0.4})`; // Green
    } else if (normalizedValue < 0.7) {
      return `rgba(255, 255, 0, ${0.4 + normalizedValue * 0.3})`; // Yellow
    } else {
      return `rgba(255, 0, 0, ${0.5 + normalizedValue * 0.4})`; // Red
    }
  };
  
  // Get threat overlay color based on opponent threat after move from score matrix
  const getThreatOverlayColor = (row, col) => {
    if (!showMoveEvaluation || !matrixCache?.scoreMatrix || board[row][col] !== null) {
      return 'transparent';
    }
    
    // Find the score data for this cell from the cached score matrix
    const cellScore = matrixCache.scoreMatrix.find(score => score.row === row && score.col === col);
    if (!cellScore) {
      return 'transparent';
    }
    
    const opponentThreatAfterMove = cellScore.opponentThreatAfterMove || 0;
    
    // Color scale: transparent (0) -> blue (low threat) -> purple (medium) -> red (high threat)
    if (opponentThreatAfterMove === 0) {
      return 'transparent';
    } else if (opponentThreatAfterMove < 1.0) {
      return `rgba(0, 150, 255, ${0.2 + opponentThreatAfterMove * 0.3})`; // Blue
    } else if (opponentThreatAfterMove < 2.0) {
      return `rgba(150, 0, 255, ${0.3 + (opponentThreatAfterMove - 1.0) * 0.4})`; // Purple
    } else {
      return `rgba(255, 0, 0, ${0.4 + Math.min((opponentThreatAfterMove - 2.0) * 0.3, 0.5)})`; // Red
    }
  };
  
  // Get Nash equilibrium overlay color
  const getNashOverlayColor = (row, col) => {
    if (!useNashAnalysis || !matrixCache?.nashAnalysis || board[row][col] !== null) {
      return 'transparent';
    }
    
    // Find the Nash data for this cell
    const cellNash = matrixCache.nashAnalysis.find(nash => nash.row === row && nash.col === col);
    if (!cellNash) {
      return 'transparent';
    }
    
    // Color based on Nash stability score
    const stabilityScore = cellNash.nashStabilityScore || 0;
    
    // Color scale: transparent (0) -> green (stable) -> yellow (neutral) -> red (unstable)
    if (stabilityScore === 0) {
      return 'transparent';
    } else if (stabilityScore > 0) {
      return `rgba(0, 255, 0, ${0.2 + Math.min(stabilityScore * 0.3, 0.5)})`; // Green for stable
    } else {
      return `rgba(255, 0, 0, ${0.2 + Math.min(Math.abs(stabilityScore) * 0.3, 0.5)})`; // Red for unstable
    }
  };

  return (
    <div 
      ref={boardRef}
      className="game-board-container"
      onMouseLeave={handleBoardMouseLeave}
    >
      <div 
        className={`game-board ${aiThinking ? 'ai-thinking' : ''}`}
        style={{
          gridTemplateColumns: `repeat(${boardSize}, ${cellSize}px)`,
          gridTemplateRows: `repeat(${boardSize}, ${cellSize}px)`,
          width: `${fixedBoardSize}px`,
          height: `${fixedBoardSize}px`,
          // Add stability for 8x8 boards
          ...(boardSize === 8 && {
            transform: 'translateZ(0)', // Force hardware acceleration
            backfaceVisibility: 'hidden',
            perspective: '1000px'
          })
        }}
      >
        {board.map((row, rowIndex) =>
          row.map((cell, colIndex) => {
            const isWinningCell = winningPositions.some(pos => pos.row === rowIndex && pos.col === colIndex);
            
            // Determine domino highlighting
            const dominoValue = dominoTiling && 
              rowIndex < dominoTiling.length && 
              colIndex < dominoTiling[rowIndex].length ? 
              dominoTiling[rowIndex][colIndex] : null;
            const isDominoCell = showDominoHighlighting && dominoTiling !== null;
            
            // Get domino styling (background color for filled dominos)
            const getDominoStyle = () => {
              if (!isDominoCell) return {};
              
              return {
                backgroundColor: getDominoColor(dominoValue),
                opacity: 0.7
              };
            };
            
            // Get domino number label
            const getDominoLabel = () => {
              if (!isDominoCell) return null;
              return dominoValue + 1; // Add 1 to make it 1-based instead of 0-based
            };
            
            // Determine what to display in this cell
            let displayContent = null;
            
            if (cell === 'X') {
              if (gameMode === 'player-player') {
                // Player vs Player - show avatar for first player
                displayContent = { type: 'avatar', value: selectedAvatar || 'X' };
              } else if (gameMode === 'player-computer') {
                // X is human player - show selected avatar
                displayContent = { type: 'avatar', value: selectedAvatar || 'X' };
              } else if (gameMode === 'computer-computer') {
                // X is AI player 1
                const player1 = getAIPlayerById(selectedAIPlayer1);
                if (player1) {
                  const title = `${player1.name || player1.playerName || 'AI'} — ${player1.description || player1.playerDescription || ''}`.trim();
                  displayContent = { type: 'image', src: `ai_players/${player1.iconFile || player1.thumbnail}`, alt: 'Player X', title, tooltip: `${player1.name || player1.playerName || 'AI'} — ${player1.description || player1.playerDescription || ''}` };
                } else {
                  displayContent = { type: 'text', value: 'X' };
                }
              }
            } else if (cell === 'O') {
              if (gameMode === 'player-player') {
                // Player vs Player - show O
                displayContent = { type: 'text', value: 'O' };
              } else if (gameMode === 'player-computer') {
                // O is AI player 2 in PvC
                const player2 = getAIPlayerById(selectedAIPlayer2);
                if (player2) {
                  const title = `${player2.name || player2.playerName || 'AI'} — ${player2.description || player2.playerDescription || ''}`.trim();
                  displayContent = { type: 'image', src: `ai_players/${player2.iconFile || player2.thumbnail}`, alt: 'Player O', title, tooltip: `${player2.name || player2.playerName || 'AI'} — ${player2.description || player2.playerDescription || ''}` };
                } else {
                  displayContent = { type: 'text', value: 'O' };
                }
              } else if (gameMode === 'computer-computer') {
                // O is AI player 2
                const player2 = getAIPlayerById(selectedAIPlayer2);
                if (player2) {
                  const title = `${player2.name || player2.playerName || 'AI'} — ${player2.description || player2.playerDescription || ''}`.trim();
                  displayContent = { type: 'image', src: `ai_players/${player2.iconFile || player2.thumbnail}`, alt: 'Player O', title, tooltip: `${player2.name || player2.playerName || 'AI'} — ${player2.description || player2.playerDescription || ''}` };
                } else {
                  displayContent = { type: 'text', value: 'O' };
                }
              }
            }
            
            return (
              <div
                key={`${rowIndex}-${colIndex}`}
                className={`board-cell ${cell ? `cell-${cell.toLowerCase()}` : ''} ${isWinningCell ? 'winning-cell' : ''} ${isDominoCell ? 'domino-cell' : ''}`}
                onClick={() => onCellClick(rowIndex, colIndex)}
                onMouseEnter={(e) => handleCellMouseEnter(rowIndex, colIndex, e)}
                onMouseLeave={handleCellMouseLeave}
                style={{
                  width: `${cellSize}px`,
                  height: `${cellSize}px`,
                  fontSize: `${Math.max(12, cellSize * 0.4)}px`,
                  ...getDominoStyle(),
                  position: 'relative'
                }}
              >
                {/* Heatmap overlay */}
                {showHeatmap && heatmapData && (
                  <div
                    className="heatmap-overlay"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      backgroundColor: getHeatmapColor(heatmapData[rowIndex][colIndex]),
                      pointerEvents: 'none',
                      zIndex: 1,
                      transition: 'opacity 0.2s'
                    }}
                  />
                )}
                
                {/* Threat overlay for move evaluation */}
                {showMoveEvaluation && (
                  <div
                    className="threat-overlay"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      backgroundColor: getThreatOverlayColor(rowIndex, colIndex),
                      pointerEvents: 'none',
                      zIndex: 2,
                      transition: 'opacity 0.2s'
                    }}
                  />
                )}
                
                {/* Nash equilibrium overlay */}
                {useNashAnalysis && (
                  <div
                    className="nash-overlay"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      backgroundColor: getNashOverlayColor(rowIndex, colIndex),
                      pointerEvents: 'none',
                      zIndex: 3,
                      transition: 'opacity 0.2s'
                    }}
                  />
                )}
                {displayContent && (
                  displayContent.type === 'image' ? (
                    <span className="ai-bubble-wrap">
                      <img 
                        src={displayContent.src} 
                        alt={displayContent.alt}
                        aria-label={displayContent.title || displayContent.alt}
                        style={{
                          width: `${cellSize}px`,
                          height: `${cellSize}px`,
                          objectFit: 'cover',
                          objectPosition: 'center 30%',
                          borderRadius: '6px',
                          imageRendering: 'high-quality',
                          imageRendering: '-webkit-optimize-contrast',
                          imageRendering: 'crisp-edges',
                          backfaceVisibility: 'hidden',
                          transform: 'translateZ(0)',
                          willChange: 'transform'
                        }}
                        onError={(e) => {
                          // If image fails to load, replace with text
                          e.target.style.display = 'none';
                          const textSpan = document.createElement('span');
                          textSpan.textContent = cell;
                          textSpan.style.fontSize = `${Math.max(16, cellSize * 0.5)}px`;
                          textSpan.style.fontWeight = 'bold';
                          e.target.parentNode.appendChild(textSpan);
                        }}
                      />
                      {displayContent.tooltip && (
                        <>
                          <span className="ai-tail"></span>
                          <span className="ai-tail small"></span>
                          <span className="ai-tooltip">{displayContent.tooltip}</span>
                        </>
                      )}
                    </span>
                  ) : displayContent.type === 'avatar' ? (
                    <span className="ai-bubble-wrap" aria-label={displayContent.title || 'You'}>
                      <span 
                        style={{ 
                          fontSize: `${Math.max(20, cellSize * 0.6)}px`, 
                          fontWeight: 'bold'
                        }}
                      >
                        {displayContent.value}
                      </span>
                      <span className="ai-tail"></span>
                      <span className="ai-tail small"></span>
                      <span className="ai-tooltip">{displayContent.title || 'You'}</span>
                    </span>
                  ) : (
                    <span 
                      style={{ 
                        fontSize: `${Math.max(16, cellSize * 0.5)}px`, 
                        fontWeight: 'bold'
                      }}
                    >
                      {displayContent.value}
                    </span>
                  )
                )}
                
                {/* Domino number label */}
                {getDominoLabel() && (
                  <div 
                    className="domino-label"
                    style={{
                      position: 'absolute',
                      top: '2px',
                      right: '2px',
                      fontSize: `${Math.max(10, cellSize * 0.25)}px`,
                      fontWeight: 'bold',
                      color: '#000',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      borderRadius: '50%',
                      width: `${Math.max(16, cellSize * 0.3)}px`,
                      height: `${Math.max(16, cellSize * 0.3)}px`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid #000'
                    }}
                  >
                    {getDominoLabel()}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
      
      {/* Move Evaluation Tooltip */}
      {(tooltipData || tooltipLoading) && (showMoveEvaluation || useNashAnalysis) && (
        <div 
          className="move-evaluation-tooltip"
          style={{
            position: 'fixed',
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '500',
            zIndex: 1000,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            minWidth: '140px'
          }}
        >
          <div style={{ marginBottom: '4px', fontWeight: 'bold' }}>
            {showMoveEvaluation && useNashAnalysis ? 'Move & Nash Overlay:' : 
             showMoveEvaluation ? 'Move Evaluation:' : 'Nash Overlay:'}
          </div>
          {tooltipLoading ? (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <div style={{ fontSize: '10px', opacity: 0.8 }}>Calculating...</div>
            </div>
          ) : (
            <>
              <div style={{ fontSize: '11px', opacity: 0.9, marginBottom: '2px' }}>Square: [{tooltipData.row ?? '?'},{tooltipData.col ?? '?'}]</div>
              
              {showMoveEvaluation && (
                <>
                  <div>My Threat: {(tooltipData.aiThreat || 0).toFixed(1)}</div>
                  <div>Opponent Threat: {(tooltipData.opponentThreat || 0).toFixed(1)}</div>
                  <div>Opponent After Move: {(tooltipData.opponentThreatAfterMove || 0).toFixed(1)}</div>
                  <div>Target Multiplicity: {tooltipData.targetMultiplicity || 0}</div>
                  <div style={{ marginTop: '4px', fontWeight: 'bold', borderTop: '1px solid rgba(255,255,255,0.3)', paddingTop: '4px' }}>
                    Weighted move score: {(tooltipData.weightedScore || 0).toFixed(1)}
                  </div>
                </>
              )}
              
              {useNashAnalysis && (
                <>
                  {showMoveEvaluation && <div style={{ marginTop: '8px', borderTop: '1px solid rgba(255,255,255,0.3)', paddingTop: '4px' }}></div>}
                  <div style={{ color: tooltipData.isNashMove ? '#00ff00' : '#ffffff' }}>
                    {tooltipData.isNashMove ? '✓ Nash Move' : '○ Not Nash'}
                  </div>
                  <div>Stability Score: {(tooltipData.nashStabilityScore || 0).toFixed(2)}</div>
                  <div>Best Response Quality: {(tooltipData.bestResponseQuality || 0).toFixed(1)}</div>
                  <div>Equilibrium Deviation: {(tooltipData.equilibriumDeviation || 0).toFixed(2)}</div>
                  {tooltipData.opponentBestMove && (
                    <div style={{ fontSize: '10px', opacity: 0.8 }}>
                      Opponent best: [{tooltipData.opponentBestMove.row},{tooltipData.opponentBestMove.col}]
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
      
      {false && aiThinking && (
        <div className="ai-thinking-overlay">
          <div className="ai-thinking-spinner"></div>
          <p>AI is thinking...</p>
        </div>
      )}
    </div>
  );
};

export default GameBoard;
